import { createSelector } from 'reselect';
import _values from 'lodash/values';

import RefData from 'ref-data';

// ######################################################################################## //
// SORT FIELDS
// ######################################################################################## //
const sortDirections = {
  ASCENDING:  'asc',
  DESCENDING: 'desc',
};

sortDirections.ALL = _.values(sortDirections);

// ######################################################################################## //
// INPUT SELECTORS
// ######################################################################################## //

const _scopedState = (state) => state.entities.displayContainer;
const isFetching = (state) => _scopedState(state).isFetching;
const hasFetched = (state) => _scopedState(state).hasFetched;
const displayContainersById = (state) => _scopedState(state).byId;
const thirdPartyProvider = (state) => _scopedState(state).thirdPartyProvider;

// ######################################################################################## //
// CONTAINER SELECTORS
// ######################################################################################## //
const displayContainers = (state) => _values(displayContainersById(state));

const displayContainerById = createSelector(
  [displayContainersById, (state, id) => id],
  (containers, id) => containers[id] || {}
);

// Some unavoidable complexity here: the following selectors are needed to ensure correct ordering of the containers.
// Correct ordering is "Root/Home" container first, pinned containers next, followed by default containers, each ordered by last updated in their respective groups
// Aside from a healthy sprinkling of createSelector to cache as much as we can, we achieve this by:
// 1. Grouping containers into an object with named keys for each group, each key storing an array of containers
// 2. Sorting each of those arrays by the lastUpdated field
// 3. Spreading those groups back out into a new array for the list to display

// Break out the containers into an object keyed for root, persistent and other containers
const _containersByListOrder = createSelector([displayContainers], (containers) => {
  return _.reduce(
    containers,
    (acc, c) => {
      switch (true) {
        case c.isDefault:
          acc.isDefault.push(c);
          break;
        case c.isRoot:
          acc.isRoot.push(c);
          break;
        case !!c.persistent:
          acc.isPinned.push(c);
          break;
        default:
          acc.isStandard.push(c);
          break;
      }
      return acc;
    },
    {
      isRoot:     [],
      isDefault:  [],
      isPinned:   [],
      isStandard: [],
    }
  );
});

const compare = (a, b, dir) => (a === b ? 0 : dir === sortDirections.ASCENDING ? (a < b ? -1 : 1) : a > b ? -1 : 1);

// Sort passed in display containers by the lastUpdated property
const sortContainerByLastUpdated = (containers = [], dir = sortDirections.ASCENDING) =>
  containers.sort((a, b) => compare(a.lastUpdated, b.lastUpdated, dir));

// provides a sort on the results of the _containersByListOrder method above
const _sortRootContainers = createSelector([_containersByListOrder], (containers) =>
  sortContainerByLastUpdated(containers.isRoot)
);
const _sortDefaultContainers = createSelector([_containersByListOrder], (containers) =>
  sortContainerByLastUpdated(containers.isDefault, sortDirections.DESCENDING)
);
const _sortPinnedContainers = createSelector([_containersByListOrder], (containers) =>
  sortContainerByLastUpdated(containers.isPinned, sortDirections.DESCENDING)
);
const _sortStandardContainers = createSelector([_containersByListOrder], (containers) =>
  sortContainerByLastUpdated(containers.isStandard, sortDirections.DESCENDING)
);

// Arrange the now grouped and sorted containers back into an array for the list to render
const channelDisplayContainerList = createSelector(
  [_sortRootContainers, _sortDefaultContainers, _sortPinnedContainers, _sortStandardContainers],
  (rootContainers, defaultContainers, pinnedContainers, standardContainers) => {
    return [...defaultContainers, ...rootContainers, ...pinnedContainers, ...standardContainers];
  }
);

const hasInstagramPublishingDestination = createSelector(
  [displayContainers],
  (displayContainers) =>
    !_.some(displayContainers, ['typeRef', 'id', RefData.Enum.TYPE.DISPLAYCONTAINER_INSTAGRAM.INNETWORK])
);

export default {
  channelDisplayContainerList,
  displayContainerById,
  displayContainers,
  hasFetched,
  isFetching,
  thirdPartyProvider,
  hasInstagramPublishingDestination,
};
